import {
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IdataArrayResponse,
  IdataBin,
  IdeleteOneResponse,
  IfilterProps,
  Isuccess,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

/**
 * Type alias for data bin properties
 * Omits 'collectionName' from IdataBin as it's handled by the base class
 */
type TdataBinProps = Omit<IdataBin, 'collectionName'>;

/**
 * Represents a soft-deleted item in the system's data bin.
 * This class provides methods to manage soft-deleted items,
 * including restoration and permanent deletion.
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link DataBin} class when invoking client operations.
 *
 * @usage
 * ```typescript\
 *
 * const dataBin = await DataBin.filterAll({
 *
 * });
 *
 * const dataBin = await DataBin.bulkDelete({
 *
 * });
 *
 * const dataBin = await DataBin.restoreMany({
 *
 * });
 *
 * const dataBin = await DataBin.remove();
 *
 * ```
 */
export class DataBin {
  /** Base API endpoint for data bin operations */
  private static readonly baseUrl = '/collections-bin';

  /** Unique identifier of the soft-deleted item */
  readonly _id: TdataBinProps['_id'];

  /** Reference to the parent document (if any) */
  readonly parent: TdataBinProps['parent'];

  /** Timestamp when the item was soft-deleted (optional) */
  readonly deletedAt?: TdataBinProps['deletedAt'];

  /** Timestamp when the item was originally created */
  readonly createdAt: TdataBinProps['createdAt'];

  /** Timestamp when the item was last updated */
  readonly updatedAt: TdataBinProps['updatedAt'];

  /** Additional information about the soft-deleted item (optional) */
  readonly info?: TdataBinProps['info'];

  /**
   * Creates a new instance of DataBin.
   * @param {TdataBinProps} data - The data to initialize the instance with
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: TdataBinProps) {
    data.createdAt = new Date(data.createdAt);
    data.updatedAt = new Date(data.updatedAt);

    validateData(data, {
      _id: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Unique identifier must be a non-empty string'
      },
      deletedAt: {
        required: false,
        validate: (v) => {
          // If deletedAt is provided, validate it
          if (v !== undefined) {
            const deletedAtDate = new Date(v);

            return !isNaN(deletedAtDate.getTime()) &&
                  deletedAtDate <= new Date();
          }

          return true;
        },
        message: 'Deleted at must be a valid date string not in the future'
      },
      createdAt: {
        validate: (v) => {
          // Convert to Date if not already a Date object
          const createdAt = v instanceof Date ? v : new Date(v);

          // Check date validity
          return !isNaN(createdAt.getTime());
        },
        message: 'Created at must be a valid date (Date object or ISO date string)'
      },
      updatedAt: {
        validate: (v) => {
          // Convert to Date if not already a Date object
          const updatedAt = v instanceof Date ? v : new Date(v);

          // Check date validity
          return !isNaN(updatedAt.getTime());
        },
        message: 'Updated at must be a valid date (Date object or ISO date string)'
      }
    });

    this._id = data._id;
    this.parent = data.parent;
    this.deletedAt = data.deletedAt;
    this.createdAt = new Date(data.createdAt);
    this.updatedAt = new Date(data.updatedAt);
    this.info = data.info;
  }

  /**
   * Retrieves soft-deleted items based on filter criteria with pagination support.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: DataBin[] }>}
   * An object containing the total count and an array of soft-deleted items
   * @throws {Error} If there's an error applying the filter or fetching the items
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TdataBinProps>>(
        `${DataBin.baseUrl}/filter`,
        filter
      );
    const edits = await lastValueFrom(observer$);

    return {
      count: edits.count,
      dataArr: edits.data.map(val => new DataBin(val))
    };
  }

  /**
   * Permanently deletes multiple soft-deleted items.
   * @param {IbulkDelete} payload - The criteria for bulk deletion
   * @returns {Promise<IbulkDeleteSuccessResponse>}
   * The result of the bulk deletion operation
   * @throws {Error} If the bulk deletion fails
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(
        `${DataBin.baseUrl}/bulk-delete`,
        payload
      );

    return lastValueFrom(observer$);
  }

  /**
   * Restores multiple soft-deleted items to their original collections.
   * @param {Object} payload - The restore criteria
   * @param {string[]} payload._ids - Array of item IDs to restore
   * @returns {Promise<Isuccess>} The result of the restore operation
   * @throws {Error} If the restore operation fails
   */
  static restoreMany(payload: {_ids: string[]}) {
    const observer$ = CommerceClient.ehttp
      .makePost<Isuccess>(
        `${DataBin.baseUrl}/restore-many`,
        payload
      );

    return lastValueFrom(observer$);
  }

  /**
   * Permanently deletes the current soft-deleted item.
   * @returns {Promise<IdeleteOneResponse | null>}
   * The result of the deletion operation
   * @throws {Error} If the deletion fails or the item doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${DataBin.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }

  /**
   * Restores the current soft-deleted item to its original collection.
   * @returns {Promise<Isuccess>} The result of the restore operation
   * @throws {Error} If the restore operation fails or the item doesn't exist
   */
  restore() {
    const observer$ = CommerceClient.ehttp
      .makePut<Isuccess>(
        `${DataBin.baseUrl}/restore/${this._id}`,
        {}
      );

    return lastValueFrom(observer$);
  }
}
